import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../config/api';
import Layout from './Layout';
import { useToast } from '../contexts/ToastContext';
import OrdersTable from './tables/OrdersTable';
import DraftsTable from './tables/DraftsTable';
import ShareDraftModal from './common/ShareDraftModal';
import ShippingContactModal from './common/ShippingContactModal';
import cartService from '../services/cart';
import '../styles/design-tokens.css';

interface Site {
  id: number;
  name: string;
  code: string;
  city?: string;
  state?: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  sites?: Site[];
}

interface Draft {
  draft_id: string;
  draft_number: string;
  status: string;
  total_amount: number;
  created_at: string;
  expires_at?: string;
  cart_snapshot?: {
    items: Array<{
      name: string;
      sku: string;
      quantity: number;
    }>;
  };
}

interface Order {
  id: string;
  order_number: string;
  status: string;
  type: string;
  payment_status: string;
  payment_method?: string;
  total_amount: number;
  created_at: string;
  sitename: string;
  customer_name: string;
}

const Dashboard: React.FC = () => {
  // Dashboard component
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [totalDrafts, setTotalDrafts] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState<Draft | null>(null);
  const [shareLink, setShareLink] = useState('');
  const [qrEnlarged, setQrEnlarged] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);

  useEffect(() => {
    // Load user from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    // Load dashboard data
    loadDashboardData();
  }, []);
  
  // Track window width for responsive layout
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load cart count
  useEffect(() => {
    const loadCartCount = async () => {
      try {
        const cart = await cartService.getCart();
        const itemCount = cart.items?.reduce((sum: number, item: any) => sum + item.quantity, 0) || 0;
        setCartItemCount(itemCount);
      } catch (error) {
        console.error('Failed to load cart count:', error);
      }
    };
    loadCartCount();
  }, []);

  const handleReorder = async (orderId: string) => {
    try {
      // Use the new backend reorder endpoint
      const response = await api.post(`/orders/${orderId}/reorder`);
      const result = response.data;

      // Show feedback to user
      if (result.items_added > 0) {
        showSuccess('Items added to cart successfully!');

        // Navigate to cart after a short delay
        setTimeout(() => {
          navigate('/cart');
        }, 1500);
      } else {
        showError('No items could be added to cart. Please check product availability.');
      }
    } catch (error) {
      console.error('Failed to reorder:', error);
      showError('Failed to add items to cart. Please try again.');
    }
  };

  const handleCheckoutDraft = async (draftId: string) => {
    try {
      navigate(`/checkout/draft/${draftId}`);
    } catch (error) {
      console.error('Failed to checkout draft:', error);
    }
  };

  const shareDraft = async (draft: Draft) => {
    // Show contact modal first to collect shipping contact info
    setSelectedDraft(draft);
    setShowContactModal(true);
  };

  const handleContactSaved = async () => {
    // Close contact modal
    setShowContactModal(false);

    // Now create the share link
    if (!selectedDraft) return;

    try {
      const response = await api.post(`/drafts/${selectedDraft.draft_id}/share`, {
        expires_in_hours: 168 // 7 days
      });
      // Use full URL with hostname
      const fullUrl = response.data.share_url.startsWith('http')
        ? response.data.share_url
        : `${window.location.origin}${response.data.share_url}`;

      console.log('Dashboard Share - API response:', response.data.share_url);
      console.log('Dashboard Share - Full URL:', fullUrl);

      setShareLink(fullUrl);
      setShowShareModal(true);

      // Auto-copy to clipboard
      await navigator.clipboard.writeText(fullUrl);
    } catch (err) {
      console.error('Failed to share draft:', err);
      showError('Failed to create share link');
    }
  };

  const handleDeleteDraft = async (draft: Draft) => {
    if (window.confirm('Are you sure you want to delete this draft?')) {
      try {
        await api.delete(`/drafts/${draft.draft_id}`);
        showSuccess('Draft deleted successfully');
        loadDashboardData(); // Refresh the dashboard data
      } catch (err) {
        console.error('Failed to delete draft:', err);
        showError('Failed to delete draft');
      }
    }
  };

  const loadDashboardData = async () => {
    try {
      const [userResponse, draftsResponse, ordersResponse] = await Promise.all([
        api.get('/users/me'),
        api.get('/drafts', {
          params: {
            page_size: 5,
            sort_by: 'created_at',
            sort_order: 'desc'
          }
        }),
        api.get('/orders', {
          params: {
            page_size: 5,
            include_quotes: false
          }
        })
      ]);
      
      setUser(userResponse.data);

      // Process drafts data
      if (draftsResponse.data.items) {
        setDrafts(draftsResponse.data.items);
        setTotalDrafts(draftsResponse.data.total || 0);

        // Process drafts without fake stats
      }

      // Process orders data
      if (ordersResponse.data.items) {
        setOrders(ordersResponse.data.items);
        setTotalOrders(ordersResponse.data.total || 0);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  const handleStartNewOrder = () => {
    // Navigate to products page without default filter
    navigate('/products');
  };

  return (
    <Layout>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '20px 1.5rem'
      }}>
          {/* Hero CTA Section */}
          <div className="mb-4" style={{
            background: 'linear-gradient(135deg, var(--color-success, #2E7D32) 0%, #1B5E20 100%)',
            borderRadius: '12px',
            padding: '14px 20px',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14px', fontWeight: '500', marginBottom: '12px', opacity: 0.95 }}>
              If ordering non-room equipment please contact us at{' '}
              <span
                style={{
                  color: 'white',
                  textDecoration: 'underline',
                  fontWeight: '600'
                }}
              >
                (888) 787-3431
              </span>
            </p>
            <button
              className="btn"
              onClick={handleStartNewOrder}
              style={{
                background: 'white',
                color: 'var(--color-success)',
                padding: '8px 24px',
                fontSize: '14px',
                fontWeight: '600',
                borderRadius: '6px',
                border: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }}
            >
              {cartItemCount > 0 ? 'Continue Shopping' : 'Start New Order'}
            </button>
          </div>

          {/* Recent Orders Section */}
          <OrdersTable
            orders={orders}
            onReorder={handleReorder}
            showAllLink={totalOrders > 5}
            compact={true}
          />

          {/* Recent Drafts Section */}
          <DraftsTable
            drafts={drafts}
            onShare={shareDraft}
            onCheckout={handleCheckoutDraft}
            onDelete={handleDeleteDraft}
            showAllLink={totalDrafts > 5}
            compact={true}
          />
        
      <ShippingContactModal
        show={showContactModal && !!selectedDraft}
        onClose={() => {
          setShowContactModal(false);
          setSelectedDraft(null);
        }}
        onSuccess={handleContactSaved}
        draftId={selectedDraft?.draft_id || ''}
        draftNumber={selectedDraft ? `DFT-${selectedDraft.draft_number.slice(-8)}` : undefined}
      />

      <ShareDraftModal
        show={showShareModal && !!selectedDraft}
        onClose={() => {
          setShowShareModal(false);
          setQrEnlarged(false);
        }}
        shareLink={shareLink}
        draftNumber={selectedDraft ? `DFT-${selectedDraft.draft_number.slice(-8)}` : undefined}
      />
      </div>
    </Layout>
  );
};

export default Dashboard;